/**
 * 잡 계약 — **"이 잡이 언제 도는가"와 "돌았는가"의 값 집합.**
 *
 * 왜 contracts 인가: 이 셋이 서로 다른 층에 있고 **같은 사실**을 말해야 한다.
 *   ① 잡 선언(`packages/job-kernel`)이 주기를 적고
 *   ② 서버가 실행 원장과 대조해 상태를 판정하고
 *   ③ 화면이 그 상태를 그린다.
 * 주기 타입이 선언 쪽에만 있으면 화면은 자기 사본을 만들게 되고, 그 사본이 어긋나면
 * 증상은 에러가 아니라 **"늦지 않았는데 늦었다고 나온다"** (또는 그 반대다).
 *
 * ─── 구 시스템이 잃은 것 ───
 * 월차 자동 부여 잡은 도입(2025-12-02) 뒤 **두 달간 한 번도 안 돌았다.** 첫 실행 이력이
 * 2026-02-01 이고 그 사이에 에러도, "안 돌았다"는 문장도 없다. 그동안 사람이 66건 155일을
 * 손으로 메웠고 사유 문구가 그 증거다('12월 월차' · '누락' · '월차 1 누락됨').
 * 실패를 알리는 것만으로는 이것을 못 잡는다 — **실패가 없었다.** 시작조차 안 했다.
 * 그래서 이 파일의 상태 집합에 `never` 와 `overdue` 가 있고, 그 둘은 실행 원장의 **부재**로
 * 판정된다. (`docs/decisions/0019`)
 */

/**
 * 제품의 시간대. 잡의 부여일·급여 달·근태 하루가 전부 한국 법정 개념이라 하나다.
 *
 * ★ 상수로 두는 이유가 실측이다: 자동 부여 잡은 **매월 1일 03:00 KST** 에 도는데
 * 그 순간의 UTC 는 **전달 말일 18:00** 이다. `new Date().toISOString().slice(0,7)` 로
 * 달을 구하면 잡은 매번 **전달을 부여한다** — 화면 버튼으로는 거의 안 밟히고
 * (사람은 한낮에 누른다) 잡에서는 100% 밟히는 형태다. 그래서 달을 만드는 함수가
 * 시간대를 **인자로 요구하고**(`monthOf`, packages/core) 그 인자의 원본이 여기다.
 */
export const PRODUCT_TZ = 'Asia/Seoul';

/**
 * 주기. **닫힌 유니온이다.**
 *
 * cron 문자열을 선언으로 두지 않은 이유: 그러면 같은 사실이 둘이 된다 — 큐에 넘길
 * 문자열과, "마지막으로 돌았어야 하는 시각"을 계산하는 코드. 여기서는 선언이 구조체 하나이고
 * cron 문자열은 **파생값**이다(`cronOf`).
 *
 * ★ 2026-09-05 에 `daily` 가 붙었다. 이 주석이 미리 적어 둔 그대로 됐다 — 유니온을 늘리자
 * 컴파일러가 읽는 자리를 **전부** 가리켰다(파생 함수 셋 + cron 자기검사 + 화면 문장 하나).
 * `kind` 를 처음부터 판별자로 둔 것이 그 값을 한 셈이고, 그래서 늘리는 비용이 "빠뜨린 곳을
 * 찾는 일"이 아니라 "컴파일러가 준 목록을 채우는 일"이었다.
 *
 * ★ 2026-09-10 에 `everyMinutes` 가 붙었다(0048). 같은 일이 두 번째로 일어났고, 이번에는
 * 컴파일러가 **일곱 자리**를 가리켰다 — 파생 함수 셋 · cron · fan-out · 선언 검사 · 화면.
 * 그리고 이번 확장이 **선언 검사를 하나 낳았다**: 유예가 주기보다 크면 "늦었다"가
 * 표현 불가능해진다(월 주기에서는 180분 유예가 자명히 작아서 안 보이던 함정이다).
 */
export interface JobScheduleMonthly {
  readonly kind: 'monthly';
  /** 1..28. 29~31 을 막는 이유는 2월이다 — "매월 31일"은 열두 달 중 일곱 달만 온다. */
  readonly dayOfMonth: number;
  readonly hour: number;
  readonly minute: number;
}

/**
 * 매일. **날짜 필드가 없는 것이 요점이다** — `monthly` 에서 `dayOfMonth` 만 빼면
 * "0 이면 매일" 같은 관례가 생기고, 그 관례는 타입에 안 적힌다.
 *
 * 하루를 관측해서 적는 잡(`core.usage.snapshot`)이 첫 사용처다. 그런 잡을 월 주기로
 * 두면 한 달 중 **하루만** 관측하고 나머지 29일은 영원히 모르는 상태가 된다 —
 * 그건 표본이지 원장이 아니다.
 */
export interface JobScheduleDaily {
  readonly kind: 'daily';
  readonly hour: number;
  readonly minute: number;
}

/**
 * **하루 안에서 반복.** 분 단위 주기이고, 첫 사용처는 미퇴근 알림이다(0048) —
 * 근무조 종료는 사람마다 다르므로 하루 한 번 도는 잡으로는 "지금 막 지났다"를 못 잡는다.
 *
 * ─── `minutes` 가 60의 약수인 것이 규약이다 ───
 * cron 의 분 스텝 표기(슬래시)는 **시(hour) 경계에서 리셋된다.** 7분 스텝은 :00 :07 … :56 다음이
 * 다음 시의 :00 이라 마지막 간격만 4분이다. 즉 60의 약수가 아니면 큐가 도는 간격과
 * `previousExpectedRunAt` 이 계산하는 간격이 **갈린다** — 그 어긋남의 증상은 에러가 아니라
 * "가끔 tick 이 원장에 두 번 들어간다"이다. 그래서 `defineJob` 이 60의 약수만 받는다.
 *
 * ─── 하한이 5분인 이유 ───
 * tick 하나가 테넌트당 원장 한 행이다(`core.job_runs`). 1분 주기면 회사 하나에 하루
 * **1,440행**이고, 그건 실행 원장이 아니라 로그다 — 그 표는 "돌았나"를 답하려고 있다.
 */
export interface JobScheduleEveryMinutes {
  readonly kind: 'everyMinutes';
  /** 5..60 이고 60의 약수. 위 주석의 두 이유가 그 범위를 만든다. */
  readonly minutes: number;
}

export type JobSchedule = JobScheduleMonthly | JobScheduleDaily | JobScheduleEveryMinutes;

/** 실행 한 건의 상태. `running` 이 끝나지 않은 채 남는 것도 사실이다(프로세스가 죽었다). */
export const JOB_RUN_STATUSES = ['running', 'succeeded', 'failed'] as const;
export type JobRunStatus = (typeof JOB_RUN_STATUSES)[number];

/**
 * 잡 하나의 건강 상태. **`never` 를 `overdue` 에 접지 않는다.**
 *
 * 둘은 사람이 할 일이 다르다: `overdue` 는 "왜 이번 달에 안 돌았나"이고
 * `never` 는 "이 회사에서 한 번도 안 돌았다" — 구 시스템이 두 달간 있던 상태이고,
 * 그때 필요한 행동은 재시도가 아니라 **워커가 떠 있는지 보는 것**이다.
 */
export const JOB_HEALTHS = ['ok', 'overdue', 'never', 'failing'] as const;
export type JobHealth = (typeof JOB_HEALTHS)[number];

/** 실행 원장 한 줄. 화면이 최근 것 하나를 그린다. */
export interface JobRunItem {
  readonly scheduledFor: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly status: JobRunStatus;
  /** 재시도 번호(1부터). 같은 tick 을 두 번 도는 것은 **같은 행의 갱신**이다. */
  readonly attempt: number;
  /**
   * 결과 문장의 파라미터. 문장 자체는 잡 선언의 `resultKey` 가 갖는다 —
   * 원장에 한국어를 저장하면 로케일이 둘이 되는 날 지난 기록이 번역되지 않는다.
   */
  readonly resultParams: Readonly<Record<string, string | number>> | null;
  /** 실패 사유. i18n 이 아니다 — 사람이 원인을 찾는 데 쓰는 원문이다. */
  readonly error: string | null;
}

/** 잡 하나의 현재. 선언(코드) + 원장(DB)의 합이다. */
export interface JobStatusItem {
  readonly jobKey: string;
  readonly labelKey: string;
  readonly moduleKey: string;
  readonly schedule: JobSchedule;
  /** 결과 문장의 i18n 키. `resultParams` 와 짝이다. */
  readonly resultKey: string;
  readonly health: JobHealth;
  /**
   * **마지막으로 돌았어야 하는 시각.** 판정의 기준선이고 화면이 그대로 보여 준다 —
   * "늦었다"만 말하면 사람은 얼마나 늦었는지를 다시 계산해야 한다.
   */
  readonly expectedAt: string;
  readonly lastSuccessAt: string | null;
  readonly lastRun: JobRunItem | null;
  /**
   * 사람이 직접 돌릴 수 있는 화면의 키. null 이면 그런 화면이 없다는 **선언**이다
   * (잡 선언이 이유를 함께 적는다 — 선택 필드가 아니다).
   *
   * 구 잡은 `Date.current.day == 1` 을 스스로 검사해서 **다른 날 손으로 돌릴 수도 없었다.**
   * 빠진 달을 메우는 방법이 사람이 66건을 손으로 넣는 것뿐이었던 이유가 그것이다.
   */
  readonly manualScreenKey: string | null;
  /**
   * 그 화면의 이름표 키. **서버가 준다** — 화면이 `manualScreenKey` 에서 만들 수 없기
   * 때문이다(규칙 42).
   *
   * ★ 0092 에서 실측으로 알았다: 자동 작업 화면이 `<module>.screen.<나머지>` 라는 규약으로
   *   키를 조립하고 있었는데, **그 규약을 안 지키는 화면이 61개 중 10개**였다
   *   (`pay.payroll.stats` → `pay.screen.stats.title` …). 그 화면들에 직접 실행이 붙는
   *   날 자동 작업 목록에 **키가 그대로** 뜬다 — 그리고 그게 실제로 일어났다.
   *
   * 화면이 매니페스트에서 찾지 않는 이유는 그대로다: 권한이 없어 그 화면이 매니페스트에
   * 없는 사람에게도 "어디서 돌릴 수 있는지"는 말해 줘야 한다. 그래서 **서버가** 조립된
   * 표면에서 꺼내 실어 준다.
   */
  readonly manualScreenLabelKey: string | null;
}

export interface JobStatusList {
  readonly items: readonly JobStatusItem[];
}

/**
 * 잡의 i18n 키는 **선언하지 않고 파생한다.** (`payrollLabelKeyOf` 와 같은 수법)
 *
 * 선언 필드로 두면 그 필드가 두 번째 원본이 되고, 화면이 부르는 키와 갈라져도
 * 아무도 모른다 — 갈라진 증상은 에러가 아니라 **화면에 키가 그대로 뜨는 것**이다.
 * `<moduleKey>.job.<rest>.label` 이므로 i18n 규약(AGENTS.md 16)의 kind 자리가 `job` 이다.
 */
export const jobLabelKeyOf = (jobKey: string): string => {
  const [owner, ...rest] = jobKey.split('.');
  return `${owner}.job.${rest.join('.')}.label`;
};

/** 결과 문장의 키. 파라미터는 실행 원장의 `result_params` 가 준다. */
export const jobResultKeyOf = (jobKey: string): string => {
  const [owner, ...rest] = jobKey.split('.');
  return `${owner}.job.${rest.join('.')}.result`;
};

/**
 * 주기 하나의 **길이(ms).** fan-out jitter 창과 유예의 상한이 이 값에서 온다 —
 * 둘 다 주기보다 크면 "늦었다"가 표현 불가능해진다(0048).
 *
 * `monthly` 는 **28일**로 센다. 달마다 다른 길이를 정확히 계산할 수도 있지만, 이 값은
 * 상한을 재는 데만 쓰이므로 **가장 짧은 달**이 안전한 쪽이다.
 *
 * ★ 2026-09-14 에 `packages/core` 에서 여기로 옮겼다(0061). 이 함수는 `JobSchedule` 의
 *   순수한 파생이라 원래 그 타입 옆이 자리이고, 옮긴 계기는 **vertical 이 잡을 선언하게
 *   된 것**이다 — `defineJob` 이 이것을 쓰는데 core 에 있으면 잡 선언 경로가 코어 구현을
 *   물고 오고, vertical 은 코어를 import 할 수 없다(규칙 1).
 *   `packages/core` 가 재export 하므로 부르던 자리는 하나도 안 바뀐다.
 */
export function schedulePeriodMs(schedule: JobSchedule): number {
  const MINUTE_MS = 60_000;
  if (schedule.kind === 'monthly') return 28 * 24 * 60 * MINUTE_MS;
  if (schedule.kind === 'daily') return 24 * 60 * MINUTE_MS;
  return schedule.minutes * MINUTE_MS;
}
